import * as z from 'zod';

import { zodDateOrString } from '../../utils/Zod';

export const FreddieMacLLRV1 = z.object({
  version: z.literal('1').describe('version'),
  /** Appears as "Servicer Loan Number" in Freddie documentation */
  losId: z.string().describe('string'),
  freddieMacLoanNumber: z.string().describe('string'),
  sellerServicerNumber: z.string().optional().describe('string'),

  /**
   * The date that the last payment affecting UPB was received
   * Maps to Payment['paymentDate'] (not createdDate)
   */
  lastPaymentReceivedDate: zodDateOrString.optional().describe('date'),

  /**
   * The due date of the last fulfilled statement as of the end of the reporting period
   */
  lpiDate: zodDateOrString.optional().describe('date'),

  /**
   * The unpaid principal balance as of the end of the reporting period
   */
  unpaidPrincipalBalance: z.number().describe('currency'),

  /**
   * Interest due using the Accounting Net Yield (ANY) method, meaning the interest due after the servicing
   * portion is taken out. From Freddie's docs:
   * > The ANY rate equals the note rate less the servicing spread. For example, if the note rate is 5 percent,
   * > and the servicing spread is 0.25 percent, the accounting net yield rate equals 4.75 percent.
   */
  interestDue: z.number().describe('currency'), // (includes scheduled, exception, and delinquent interest)

  /**
   * Principal received during the reporting period, according to the createdDate of payments made.
   * This reflects **actual** principal received in the period.
   *
   * From Freddie's docs:
   * > For Mortgages accounted for under the net yield method, report the Freddie Mac share of principal payments,
   * > including prepaid principal, curtailments, reduction of negative amortization, payoffs and foreclosure sale
   * > proceeds.
   */
  principalDue: z.number().describe('currency'),

  /** Exception codes are listed here: https://guide.freddiemac.com/app/guide/exhibit/60 */
  exceptionCode: z
    .enum(['40', '50', '60', '61', '65', '66', '67', '70', '71', '72', '73', '80', '91'])
    .optional()
    .describe('enum'),

  /**
   * The date of the related exception (the date entered, not the createdDate)
   */
  exceptionDate: zodDateOrString.optional().describe('date'),
  noteholder: z.enum(['0', '1', '2']).optional().describe('enum'),
  /** The current annual rate of interest the Borrower is paying on the Mortgage */
  mortgageNoteRate: z.number().describe('interestRate'),
  /**
   * The principal and interest (P&I) portion of the Borrower's regular installment for the forecast month
   * E.g., if we are reporting the July cycle, we would use the paymentHistoryConfig effective August 1.
   * (Note: 100% of the P&I amount should be entered in this field, not Freddie Mac's share.)
   */
  mortgagePrincipalInterest: z.number().describe('currency'),

  /** Deferment-related fields */

  /**
   * For Mortgages with a partial principal forbearance, the amount of the interest-bearing unpaid principal
   * balance (the amortizing principal balance) as of the end of the Accounting Cycle.
   */
  interestBearingUPB: z.number().optional().describe('currency'),
  /**
   * For Mortgages with a partial principal forbearance, the amount of the deferred (non-interest-bearing) unpaid
   * principal balance as of the end of the Accounting Cycle.
   */
  deferredUPB: z.number().optional().describe('currency'),
  /** The amount of any principal curtailment applied to the Deferred UPB during the Accounting Cycle. */
  deferredPrincipalCurtailmentAmount: z.number().optional().describe('currency'),

  /** HAMP fields */

  /**
   * The amount of any Borrower incentive payments paid by Freddie Mac and applied to the Interest-Bearing UPB
   * of the Mortgage during the Accounting Cycle.
   */
  borrowerIncentiveCurtailmentAmount: z.number().optional().describe('currency'),

  /** Prepayment penalty amount collected during the Accounting Cycle. */
  prepayPenalty: z.number().optional().describe('currency'),

  /** Interest portion attributable to an exception event (e.g., partial-month interest at payoff). */
  exceptionInterest: z.number().optional().describe('currency'),

  /** Scheduled monthly interest reported for the cycle. */
  monthlyInterest: z.number().optional().describe('currency'),

  /** Indicates whether this report is a correction of a previously submitted record. */
  correctionIndicator: z.string().optional().describe('string'),

  /** Date the loan transitioned to "inactive" status (Freddie LLR exception code 40). */
  dateLoanInactivated: zodDateOrString.optional().describe('date'),

  /**
   * Accounting Net Yield (ANY) rate — the note rate less the servicing spread.
   */
  netYieldRate: z.number().optional().describe('interestRate'),

  /** Freddie Mac's single-loan participation share, expressed as a whole-number percentage 0-100. */
  fhlmcParticipationPercent: z.number().optional().describe('enum'),

  /** Date Freddie Mac funded the purchase of the loan. */
  fundingDate: zodDateOrString.optional().describe('date'),

  /** Freddie's remittance type (e.g., "A" = ARC, "G" = Gold). */
  remittanceOption: z.string().optional().describe('string'),

  /** Number of days for the ARC remittance accounting cycle. */
  arcDays: z.number().optional().describe('number'),

  /** Principal collected during the Accounting Cycle. */
  principalCollected: z.number().optional().describe('currency'),

  /** ARM rate-change history — Net Yield Rate effective on the corresponding rate change date. */
  netYieldRate1: z.number().optional().describe('interestRate'),
  netYieldRate2: z.number().optional().describe('interestRate'),
  netYieldRate3: z.number().optional().describe('interestRate'),
  netYieldRate4: z.number().optional().describe('interestRate'),
  netYieldRate5: z.number().optional().describe('interestRate'),
  netYieldRate6: z.number().optional().describe('interestRate'),

  /** ARM rate-change history — date on which the corresponding Net Yield Rate took effect. Format: MMYY. */
  rateChangeDate1: zodDateOrString.optional().describe('date'),
  rateChangeDate2: zodDateOrString.optional().describe('date'),
  rateChangeDate3: zodDateOrString.optional().describe('date'),
  rateChangeDate4: zodDateOrString.optional().describe('date'),
  rateChangeDate5: zodDateOrString.optional().describe('date'),
  rateChangeDate6: zodDateOrString.optional().describe('date'),

  /** Single-space filler between Rate Change Date 3 and Net Yield Rate 4, per Freddie's spec (position 198). */
  filler: z.literal(' ').describe('filler'),
});
export type FreddieMacLLRV1 = z.infer<typeof FreddieMacLLRV1>;

export const FreddieMacLLR = FreddieMacLLRV1;
export type FreddieMacLLR = z.infer<typeof FreddieMacLLR>;
/** Make `FreddieMacLLRReport` a discriminated union on 'version' when changes happen */
// export const FreddieMacLLRV2 = z.object({
//   version: z.literal('2'),
//   foo: z.string(),
// });
// export const FreddieMacLLR = z.discriminatedUnion('version', [FreddieMacLLRV1, FreddieMacLLRV02012025]);
